import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import { useEffect } from 'react';

/**
 * @typedef {Object} BreadcrumbSegment
 * @property {string} label - Display text for the breadcrumb
 * @property {string} [href] - Optional navigation link (omit for current page)
 */

/**
 * @typedef {Object} BreadcrumbContextValue
 * @property {BreadcrumbSegment[]} breadcrumbs - Current breadcrumb segments
 * @property {function} setBreadcrumbs - Function to set breadcrumb segments
 * @property {string} description - Current page description
 * @property {function} setDescription - Function to set page description
 */

/** @type {React.Context<BreadcrumbContextValue>} */
const BreadcrumbContext = createContext({
  breadcrumbs: [],
  setBreadcrumbs: () => {},
  description: '',
  setDescription: () => {},
});

/**
 * Provider component for breadcrumb state management.
 * Wrap your Layout or App with this provider.
 */
export function BreadcrumbProvider({ children }) {
  const [breadcrumbs, setBreadcrumbsState] = useState([]);
  const [description, setDescriptionState] = useState('');

  const setBreadcrumbs = useCallback((segments) => {
    setBreadcrumbsState(segments);
  }, []);

  const setDescription = useCallback((desc) => {
    setDescriptionState(desc);
  }, []);

  const value = useMemo(
    () => ({ breadcrumbs, setBreadcrumbs, description, setDescription }),
    [breadcrumbs, setBreadcrumbs, description, setDescription]
  );

  return (
    <BreadcrumbContext.Provider value={value}>
      {children}
    </BreadcrumbContext.Provider>
  );
}

/**
 * Hook to read the current breadcrumb value and description.
 * Use this in components that need to display breadcrumbs (e.g., Header).
 * @returns {{ breadcrumbs: BreadcrumbSegment[], description: string }}
 */
export function useBreadcrumbValue() {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbValue must be used within a BreadcrumbProvider');
  }
  return { breadcrumbs: context.breadcrumbs, description: context.description };
}

/**
 * Hook to set breadcrumbs and optional description for the current page.
 * Call this in page components to define their breadcrumb trail.
 *
 * @param {BreadcrumbSegment[]} segments - Array of breadcrumb segments
 * @param {string} [description] - Optional short description shown below the breadcrumbs
 * @example
 * // Single segment (top-level page)
 * useBreadcrumbs([{ label: 'Dashboard' }], 'Overview of your forecasting activity');
 *
 * // Multi-segment with back navigation
 * useBreadcrumbs([
 *   { label: 'Users', href: '/users/list-users' },
 *   { label: 'John Smith' }
 * ]);
 */
export function useBreadcrumbs(segments, description = '') {
  const context = useContext(BreadcrumbContext);
  if (!context) {
    throw new Error('useBreadcrumbs must be used within a BreadcrumbProvider');
  }

  const { setBreadcrumbs, setDescription } = context;
  const segmentsRef = useRef(segments);
  segmentsRef.current = segments;
  const descriptionRef = useRef(description);
  descriptionRef.current = description;

  // Use JSON string as dependency so the effect only fires when content changes,
  // not every time a new array reference is created (inline literal on each render).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const segmentsKey = JSON.stringify(segments);

  useEffect(() => {
    setBreadcrumbs(segmentsRef.current);
    setDescription(descriptionRef.current);
    return () => {
      setBreadcrumbs([]);
      setDescription('');
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setBreadcrumbs, setDescription, segmentsKey, description]);
}

export default BreadcrumbContext;
